import { useContext, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import type { ColumnDef } from '@tanstack/react-table'
import { EyeSlashIcon } from '@heroicons/react/24/solid'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { Field, FieldLabel } from 'app/components/ui/field'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog'
import { PageScroll, PageGutterContext } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import { gqlFetchList } from 'app/lib/graphqlClient'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import WorkspaceSelector from 'app/components/WorkspaceSelector'
import LocationSelector from 'app/components/LocationSelector'
import type { LocationJson, WorkspaceJson } from 'app/types/entities'
import { useStoredCredentials, type StoredCredentialFormData, type StoredCredentialRow } from './useStoredCredentials'
import { cn } from 'app/lib/utils'

function MaskedPassword({
  revealed,
  revealing,
  onReveal,
  onHide,
}: {
  revealed: string | undefined
  revealing: boolean
  onReveal: () => void
  onHide: () => void
}) {
  if (revealed === undefined) {
    return (
      <Button variant="tertiary" size="xs" loading={revealing} onClick={onReveal}>
        Reveal
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm">{revealed}</span>
      <Button variant="icon" size="icon-sm" onClick={onHide} aria-label="Hide password">
        <EyeSlashIcon className="h-4 w-4" />
      </Button>
    </div>
  )
}

function CredentialFormModal({
  open,
  initial,
  onSubmit,
  onCancel,
}: {
  open: boolean
  initial?: StoredCredentialRow
  onSubmit: (data: StoredCredentialFormData) => Promise<void>
  onCancel: () => void
}) {
  const [label, setLabel] = useState(initial?.label ?? ``)
  const [username, setUsername] = useState(initial?.username ?? ``)
  const [password, setPassword] = useState(``)
  const [metadata, setMetadata] = useState(initial?.metadata ?? ``)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({ label, username, password, metadata: metadata || undefined })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit Credential` : `Add Credential`}</DialogTitle>
          <DialogDescription>
            {initial
              ? `Update this stored credential. Leave the password blank to keep the current one.`
              : `Store a credential for this workspace. The password is encrypted at rest.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="stored-credential-form">
          <Field>
            <FieldLabel htmlFor="stored-credential-label">Label</FieldLabel>
            <Input id="stored-credential-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
          </Field>
          <Field>
            <FieldLabel htmlFor="stored-credential-username">Username</FieldLabel>
            <Input
              id="stored-credential-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="stored-credential-password">Password</FieldLabel>
            <Input
              id="stored-credential-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={initial ? `Leave blank to keep current password` : undefined}
              required={!initial}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="stored-credential-metadata">Metadata (optional)</FieldLabel>
            <Textarea
              id="stored-credential-metadata"
              rows={2}
              value={metadata}
              onChange={(e) => setMetadata(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="tertiary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={submitting}>
              {initial ? `Save` : `Create`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const StoredCredentialsComponent = observer(function StoredCredentialsComponent(): React.ReactElement {
  const gutter = useContext(PageGutterContext)
  const vm = useStoredCredentials()

  const handleWorkspaceChange = (workspace: WorkspaceJson) => {
    if (workspace?.id) vm.onWorkspaceChange(workspace)
  }

  // Flexible workspace typeahead: the default selector sends the raw text to a
  // query_string match, so a single letter like "A" only matches workspaces
  // whose name has a standalone "A" token (e.g. "A Caring Dentist") and misses
  // prefixes like "Accelerated". Wrap the input as a name substring wildcard so
  // partial matches work, mirroring the ops location search convention.
  const workspaceFetcher = useMemo(
    () => (params: { query: string }) => {
      // Strip query_string operators from user input so it cannot break the
      // wildcard query, then wrap as a case-insensitive name substring match.
      const q = params.query
        ?.trim()
        .toLowerCase()
        .replace(/[+\-=&|><!(){}[\]^"~*?:\\/]/g, ``)
      const search = q ? `name:*${q}*` : undefined
      return gqlFetchList('workspaces', { search })
    },
    [],
  )

  const handleLocationChange = (location: LocationJson) => {
    if (location?.id) vm.onLocationChange(location)
    else vm.onClearLocation()
  }

  const editingCredential = vm.editingId ? vm.credentials.find((c) => c.id === vm.editingId) : undefined

  const columns: ColumnDef<StoredCredentialRow, any>[] = [
    {
      accessorKey: `label`,
      header: `Label`,
      id: `Label`,
      meta: {
        defaultVisible: true,
      },
    },
    {
      accessorKey: `username`,
      header: `Username`,
      id: `Username`,
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `Password`,
      header: `Password`,
      enableSorting: false,
      meta: {
        defaultVisible: true,
      },
      cell: (props) => {
        const credential = props.row.original
        return (
          <MaskedPassword
            revealed={vm.revealedPasswords[credential.id]}
            revealing={vm.revealingId === credential.id}
            onReveal={() => vm.reveal(credential.id)}
            onHide={() => vm.hideRevealed(credential.id)}
          />
        )
      },
    },
    {
      accessorKey: `locationId`,
      header: `Location`,
      id: `Location`,
      meta: {
        defaultVisible: true,
      },
      cell: (props) => vm.locationMap[props.getValue() as string] ?? props.getValue() ?? `—`,
    },
    {
      id: `actions`,
      header: ``,
      enableSorting: false,
      meta: {
        defaultVisible: true,
      },
      cell: (props) => {
        const credential = props.row.original
        return (
          <div className="flex justify-end gap-2">
            <Button variant="tertiary" size="xs" onClick={() => vm.startEdit(credential.id)}>
              Edit
            </Button>
            <Button variant="danger" size="xs" onClick={() => vm.deleteHandler(credential.id, credential.label)}>
              Delete
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <PageScroll
      variant="table"
      // Outside the Ops well main already pads this page, so PageScroll's own gutters are
      // stripped; inside the well nothing else pads it, so they stay.
      className={cn(gutter === `page` && `[&>div]:px-0 [&>div:first-child]:py-0`)}
      header={
        <PageHeaderComponent
          title="Stored Credentials"
          otherButtons={[
            <WorkspaceSelector
              key="workspace"
              workspace={vm.workspace}
              withBlankOption={false}
              showSearchInDropdown
              fetcher={workspaceFetcher}
              onChange={handleWorkspaceChange}
            />,
            ...(vm.workspace
              ? [
                  <LocationSelector
                    key="location"
                    location={vm.location}
                    filterLocations={vm.workspace?.id ? { workspaceId: vm.workspace.id } : undefined}
                    disabled={vm.locationSelectorDisabled}
                    onChange={handleLocationChange}
                  />,
                ]
              : []),
          ]}
          primaryButtonSlot={
            vm.workspace ? (
              <Button variant="primary" onClick={vm.startCreate}>
                Add Credential
              </Button>
            ) : undefined
          }
        />
      }
    >
      {vm.isEditing && (
        <CredentialFormModal
          key={vm.editingId ?? `new`}
          open={vm.isEditing}
          initial={editingCredential}
          onSubmit={vm.submitHandler}
          onCancel={vm.cancelEdit}
        />
      )}

      <SimpleTable<StoredCredentialRow>
        // key={vm.credentials?.length ?? `no-workspace`}
        columns={columns}
        data={vm.credentials}
        hideFooter
        edgePadded
        emptyState={
          !vm.workspace ? (
            <TableEmptyState title="Select a workspace" description="Choose a workspace to view stored credentials." />
          ) : vm.error ? (
            <TableEmptyState title="Error" description={vm.error} />
          ) : (
            <TableEmptyState
              title="No stored credentials"
              description="This workspace has no stored credentials yet."
              action={
                <Button variant="primary" onClick={vm.startCreate}>
                  Add Credential
                </Button>
              }
            />
          )
        }
      />
    </PageScroll>
  )
})
